import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const fmtWhen = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso.slice(0, 16);
  }
};

export const NotificationBell = () => {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 30 } });
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {
      /* silencioso si sesión aún no lista */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [load]);

  const openItem = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* */ }
    }
    setOpen(false);
    load();
    if (n.url) nav(n.url);
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    try {
      await api.delete(`/notifications/${id}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <DropdownMenu open={open} onOpenChange={(v) => { setOpen(v); if (v) load(); }}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="notification-bell-btn"
          className="relative flex h-10 w-10 items-center justify-center rounded-full border border-black/5 bg-white text-ink shadow-sm transition-transform active:scale-95"
          aria-label="Notificaciones"
        >
          <Bell className="h-4.5 w-4.5 h-[18px] w-[18px]" />
          {unread > 0 && (
            <span
              data-testid="notification-unread-badge"
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-train px-1 text-[10px] font-bold text-white"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[min(100vw-2rem,22rem)] rounded-2xl p-0" data-testid="notification-dropdown">
        <div className="flex items-center justify-between px-3 py-2.5">
          <DropdownMenuLabel className="p-0 text-sm font-bold">Notificaciones</DropdownMenuLabel>
          {unread > 0 && (
            <button type="button" onClick={markAll} data-testid="notification-read-all-btn" className="flex items-center gap-1 text-xs font-semibold text-stone-500 hover:text-ink">
              <CheckCheck className="h-3.5 w-3.5" /> Marcar leídas
            </button>
          )}
        </div>
        <DropdownMenuSeparator className="m-0" />
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-stone-500" data-testid="notification-empty">No hay notificaciones</p>
          ) : items.map((n) => (
            <DropdownMenuItem
              key={n.id}
              data-testid={`notification-item-${n.id}`}
              onSelect={(e) => { e.preventDefault(); openItem(n); }}
              className={`cursor-pointer flex-col items-stretch gap-0.5 rounded-none px-3 py-2.5 ${n.read ? "opacity-70" : "bg-train-soft/40"}`}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-ink">{n.title}</p>
                <button type="button" onClick={(e) => remove(e, n.id)} className="shrink-0 rounded p-1 text-stone-400 hover:bg-stone-100 hover:text-ink" aria-label="Eliminar">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              {n.body && <p className="text-xs text-stone-600 line-clamp-2">{n.body}</p>}
              <p className="text-[10px] text-stone-400">{fmtWhen(n.created_at)}</p>
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
